import { LOCAL_STORAGE } from "@/constants";
import { ALL_STRUCTURED_SEARCH_URL_PARAM_KEYS } from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import {
  BuiltInTool,
  ChatTemplate,
  CompletionTemplate,
  Function_,
  Message,
  PromptLayerMcpTool,
  SystemMessage,
  Template,
  TemplateFormat,
  Tool,
  ToolVariable,
  UserMessage,
} from "@/types";

export const dictToArrayOfDicts = (dict: Record<string, unknown>) =>
  Object.keys(dict).map(key => ({ key, value: dict[key] }));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const arrayOfDictsToDict = (array: { key: string; value: any }[]) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  array.reduce((acc: any, curr: any) => {
    acc[curr.key] = curr.value;
    return acc;
  }, {});

export default function getSubdomain() {
  if (window.location.host === "localhost:3000" || window.location.host === "127.0.0.1:3000") {
    return "www";
  }
  return window.location.host.split(".")[0];
}

export async function copyTextToClipboard(text: string) {
  if ("clipboard" in navigator) {
    return await navigator.clipboard.writeText(text);
  }
  return document.execCommand("copy", true, text);
}

export const formatPrice = (price: number) => {
  if (price === 0) {
    return "$0";
  }
  // Use up to 10 decimal places for very small values
  const formatted = parseFloat(price.toFixed(10));
  return `$${formatted}`;
};

export const URL_PARAM_KEYS = {
  METADATA: "metadata",
  RESOURCE: "resource",
  SCORES: "scores",
  TAGS: "tags",
  FAVORITES: "favorites",
  Q: "q",
  TRACE_Q: "trace_q",
  START_TIME: "startTime",
  END_TIME: "endTime",
  REQUESTS_START_TIME: "requests_start_time",
  REQUESTS_END_TIME: "requests_end_time",
  TRACES_START_TIME: "traces_start_time",
  TRACES_END_TIME: "traces_end_time",
  LIVE_LOGS_START_TIME: "live_logs_start_time",
  LIVE_LOGS_END_TIME: "live_logs_end_time",
  PROMPT_TEMPLATE: "promptTemplate",
  DEFAULT_DATE_RANGE_LIMIT_IS_ON: "defaultDateRangeLimitIsOn",
  INVITE: "invite",
  MODE: "mode",
  STRUCTURED_FILTERS: "structuredFilters",
  PAGE: "page",
  PAGE_SIZE: "page_size",
  SEARCH_CONTEXT: "searchContext",
  SEARCH_CONTEXT_PROMPT_ID: "searchContextPromptId",
  SEARCH_CONTEXT_PROMPT_VERSION_NUMBER: "searchContextPromptVersionNumber",
  SEARCH_CONTEXT_WORKFLOW_ID: "searchContextWorkflowId",
  SEARCH_CONTEXT_WORKFLOW_VERSION_NUMBER: "searchContextWorkflowVersionNumber",
};

export type DateRangeSearchParamNamespace = "default" | "requests" | "traces" | "liveLogs";

const DATE_RANGE_PARAM_KEYS: Record<DateRangeSearchParamNamespace, { start: string; end: string }> =
  {
    default: {
      start: URL_PARAM_KEYS.START_TIME,
      end: URL_PARAM_KEYS.END_TIME,
    },
    requests: {
      start: URL_PARAM_KEYS.REQUESTS_START_TIME,
      end: URL_PARAM_KEYS.REQUESTS_END_TIME,
    },
    traces: {
      start: URL_PARAM_KEYS.TRACES_START_TIME,
      end: URL_PARAM_KEYS.TRACES_END_TIME,
    },
    liveLogs: {
      start: URL_PARAM_KEYS.LIVE_LOGS_START_TIME,
      end: URL_PARAM_KEYS.LIVE_LOGS_END_TIME,
    },
  };

export const getDateRangeParamKeys = (namespace: DateRangeSearchParamNamespace) =>
  DATE_RANGE_PARAM_KEYS[namespace];

export const readDateRangeParamValues = (
  searchParams: URLSearchParams,
  namespaces: DateRangeSearchParamNamespace[],
): {
  startTimeParam: string | null;
  endTimeParam: string | null;
  namespace: DateRangeSearchParamNamespace | null;
} => {
  for (const namespace of namespaces) {
    const { start, end } = getDateRangeParamKeys(namespace);
    const startTimeParam = searchParams.get(start);
    const endTimeParam = searchParams.get(end);
    if (startTimeParam !== null || endTimeParam !== null) {
      return {
        startTimeParam,
        endTimeParam,
        namespace,
      };
    }
  }

  return {
    startTimeParam: null,
    endTimeParam: null,
    namespace: null,
  };
};

export const readDateRangeFromSearchParams = (
  searchParams: URLSearchParams,
  namespaces: DateRangeSearchParamNamespace[],
): {
  startTime: Date | null;
  endTime: Date | null;
  namespace: DateRangeSearchParamNamespace | null;
} => {
  const { startTimeParam, endTimeParam, namespace } = readDateRangeParamValues(
    searchParams,
    namespaces,
  );

  return {
    startTime: startTimeParam ? new Date(startTimeParam) : null,
    endTime: endTimeParam ? new Date(endTimeParam) : null,
    namespace,
  };
};

export const setDateRangeSearchParams = (
  searchParams: URLSearchParams,
  namespace: DateRangeSearchParamNamespace,
  dateRange: { from?: Date; to?: Date } | undefined,
): URLSearchParams => {
  const params = new URLSearchParams(searchParams);
  const { start, end } = getDateRangeParamKeys(namespace);

  if (dateRange?.from && dateRange?.to) {
    params.set(start, dateRange.from.toISOString());
    params.set(end, dateRange.to.toISOString());
  } else {
    params.delete(start);
    params.delete(end);
  }

  return params;
};

const ALL_RELEVANT_URL_PARAM_KEYS = [
  ...Object.values(URL_PARAM_KEYS),
  ...Object.values(DATE_RANGE_PARAM_KEYS).flatMap(({ start, end }) => [start, end]),
  ...ALL_STRUCTURED_SEARCH_URL_PARAM_KEYS,
];

export const parseUrlParams = (
  urlParams: URLSearchParams,
): {
  tags: string[];
  metadata: { key: string; value: string }[];
  favorites: string;
  query: string | null;
  scores: { name: string; operator: string; value: number }[];
  startTime: Date | null;
  endTime: Date | null;
  promptTemplate: { name: string; version_numbers?: number[] } | null;
} => {
  let tags = urlParams.getAll(URL_PARAM_KEYS.TAGS);
  if (tags.length === 1 && tags[0]?.startsWith("[") && tags[0].endsWith("]")) {
    try {
      tags = JSON.parse(tags[0]);
    } catch {
      console.error("Error parsing tags"); // eslint-disable-line no-console
    }
  }

  let metadata: {
    key: string;
    value: string;
  }[] = [];
  const metadataParam = urlParams.getAll(URL_PARAM_KEYS.METADATA);
  if (metadataParam.length > 0) {
    try {
      metadata = JSON.parse(metadataParam[0]) as {
        key: string;
        value: string;
      }[];
    } catch {
      console.error("Error parsing metadata"); // eslint-disable-line no-console
    }
  }

  let scores: { name: string; operator: string; value: number }[] = [];
  const scoresParam = urlParams.getAll(URL_PARAM_KEYS.SCORES);
  if (scoresParam.length > 0) {
    try {
      scores = JSON.parse(scoresParam[0]) as {
        name: string;
        operator: string;
        value: number;
      }[];
    } catch {
      console.error("Error parsing scores"); // eslint-disable-line no-console
    }
  }

  const favorites = urlParams.get(URL_PARAM_KEYS.FAVORITES) || "";
  const query = urlParams.get(URL_PARAM_KEYS.Q);

  const startTimeParam = urlParams.get(URL_PARAM_KEYS.START_TIME);
  const endTimeParam = urlParams.get(URL_PARAM_KEYS.END_TIME);
  const startTime = startTimeParam ? new Date(startTimeParam) : null;
  const endTime = endTimeParam ? new Date(endTimeParam) : null;

  let promptTemplate = null;
  const promptTemplateParam = urlParams.get(URL_PARAM_KEYS.PROMPT_TEMPLATE);
  if (promptTemplateParam) {
    try {
      promptTemplate = JSON.parse(promptTemplateParam) as {
        name: string;
        version_numbers?: number[];
      } | null;
    } catch {
      console.error("Error parsing prompt template"); // eslint-disable-line no-console
    }
  }

  return {
    tags,
    metadata,
    favorites,
    query,
    scores,
    startTime,
    endTime,
    promptTemplate,
  };
};

export const removeTrailingCommas = (str: string) => {
  const regex = /,(?!\s*?[{["'\w])/g;
  return str.replace(regex, "");
};

export const keepRelevantSearchParams = (
  searchParams: URLSearchParams,
  additionalKeys: string[] = [],
) => {
  const params = new URLSearchParams(searchParams);
  for (const key of params.keys()) {
    if (!ALL_RELEVANT_URL_PARAM_KEYS.includes(key) && !additionalKeys.includes(key)) {
      params.delete(key);
    }
  }
  return params.toString();
};

export const getStringContent = (prompt: CompletionTemplate | Message) => {
  if ("role" in prompt && prompt.role === "placeholder") {
    return prompt.name;
  }
  if (!prompt.content || !Array.isArray(prompt.content)) {
    return "";
  }
  return prompt.content.map(item => (item.type === "text" ? item.text : "")).join("");
};

export const getMediaContent = (prompt: CompletionTemplate | Message) => {
  if (!prompt.content || !Array.isArray(prompt.content)) {
    return [];
  }
  return prompt.content.filter(item => item.type === "media").map(item => item.media);
};

export const getThinkingContent = (prompt: CompletionTemplate | Message): string => {
  if (!prompt.content || !Array.isArray(prompt.content)) {
    return "";
  }
  return prompt.content
    .filter(item => item.type === "thinking")
    .map(item => item.thinking)
    .join("");
};

export const getSystemMessage = (chatPrompt: ChatTemplate) => {
  const systemMessage = chatPrompt.messages.find(({ role }) => role === "system");
  if (systemMessage) {
    return systemMessage as SystemMessage;
  }
  return null;
};

export const getFirstChatMessage = (chatPrompt: ChatTemplate) => {
  const firstMessage = chatPrompt.messages[0];
  if (firstMessage) {
    return firstMessage;
  }
  return null;
};

/**
 * Given an array of format strings, return the most common one.
 * Falls back to `fallback` (default "f-string") when the array is empty.
 */
export const getMajorityFormat = (
  formats: (string | undefined)[],
  fallback: TemplateFormat = "f-string",
): TemplateFormat => {
  if (formats.length === 0) {
    return fallback;
  }
  const filtered = formats.filter((f): f is string => !!f);
  if (filtered.length === 0) {
    return fallback;
  }
  const unique = [...new Set(filtered)];
  if (unique.length === 1) {
    return unique[0] as TemplateFormat;
  }
  const counts: Record<string, number> = {};
  for (const fmt of filtered) {
    counts[fmt] = (counts[fmt] || 0) + 1;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (sorted[0]?.[0] as TemplateFormat) ?? fallback;
};

export const getTemplateFormat = (promptTemplate: Template): TemplateFormat => {
  if (promptTemplate?.type === "completion") {
    return promptTemplate.template_format;
  }
  if (promptTemplate?.messages?.length) {
    const formats = promptTemplate.messages.map(msg => msg.template_format);
    return getMajorityFormat(formats);
  }
  return "f-string";
};

export const getFunctionCall = (promptTemplate: ChatTemplate) => {
  if (promptTemplate.function_call) {
    return promptTemplate.function_call;
  }

  if (promptTemplate.tool_choice) {
    if (typeof promptTemplate.tool_choice === "string") {
      return promptTemplate.tool_choice;
    }
    return promptTemplate.tool_choice.function;
  }
};

export const getFunctions = (promptTemplate: ChatTemplate) => {
  if (promptTemplate.functions && promptTemplate.functions.length > 0) {
    return promptTemplate.functions;
  }
  if (promptTemplate.tools && promptTemplate.tools.length > 0) {
    return promptTemplate.tools.map(tool => (tool.type === "function" ? tool.function : tool));
  }
  return [];
};

export const getFunctionsType = (promptTemplate: ChatTemplate) => {
  if (promptTemplate.tools) {
    return "tools";
  }
  return "functions";
};

// The response is the last message only if it's an assistant message (the LLM's actual output).
// Prior assistant messages in multi-turn conversations are part of the request context, not the response.
export const getResponseMessage = (chatTemplate: ChatTemplate) => {
  const lastMessage = chatTemplate.messages.at(-1);
  return lastMessage?.role === "assistant" ? lastMessage : undefined;
};

// Request messages are everything except the response (if one exists).
export const getRequestMessages = (chatTemplate: ChatTemplate) => {
  const { messages } = chatTemplate;
  return getResponseMessage(chatTemplate) ? messages.slice(0, -1) : messages;
};

export const getMediaVariable = (message: UserMessage): string => {
  const mediaVariable = message.content?.filter(m => m.type === "media_variable").at(0);
  return mediaVariable ? mediaVariable.name : "";
};

export const formatJson = (json: string) => {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};
export const getLastMetadataFieldFilter = (): string | null => {
  const filter = localStorage.getItem(LOCAL_STORAGE.LAST_METADATA_FIELD_FILTER) || null;
  return filter;
};

export const setLastMetadataFieldFilter = (filter: string) => {
  localStorage.setItem(LOCAL_STORAGE.LAST_METADATA_FIELD_FILTER, filter);
};

export const getSavedTemplateFormatOrDefault = (): TemplateFormat => {
  const format =
    (localStorage.getItem(LOCAL_STORAGE.LAST_TEMPLATE_FORMAT) as TemplateFormat) || "f-string";
  return format;
};

export const setSavedTemplateFormat = (format: TemplateFormat) => {
  localStorage.setItem(LOCAL_STORAGE.LAST_TEMPLATE_FORMAT, format);
};

export const compareArrays = <T>(arr1: T[], arr2: T[]) =>
  arr1.length === arr2.length && arr1.every((element, index) => element === arr2[index]);

export const getTruncatedFolderPath = (folderPath: string[]) => {
  if (folderPath?.length >= 5) {
    const newPath = folderPath.slice(-5);
    newPath.unshift("...");
    return newPath;
  }
  return folderPath;
};
export const isValidUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol === "data:") {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const createBlobUrlFromBase64 = (dataUrl: string, type: string) => {
  try {
    const base64Data = dataUrl.split(",")[1];
    const binary = atob(base64Data);
    const array = Uint8Array.from(binary, char => char.charCodeAt(0));
    const blob = new Blob([array], { type });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

export const isBuiltInTool = (func: Function_): func is BuiltInTool => {
  if (!("type" in func)) {
    return false;
  }
  const isLegacyOpenAINativeMcp =
    func.type === "mcp" &&
    "provider" in func &&
    (func.provider === "openai" || func.provider === "openai.azure") &&
    "config" in func;
  return (
    isLegacyOpenAINativeMcp ||
    func.type === "file_search" ||
    func.type === "web_search" ||
    func.type === "code_interpreter" ||
    func.type === "image_generation" ||
    func.type === "google_maps" ||
    func.type === "url_context" ||
    func.type === "openai_mcp" ||
    func.type === "bash" ||
    func.type === "shell" ||
    func.type === "apply_patch" ||
    func.type === "text_editor" ||
    // OpenRouter server tools (https://openrouter.ai/docs/guides/features/server-tools).
    func.type === "datetime" ||
    func.type === "web_fetch" ||
    func.type === "fusion" ||
    func.type === "advisor" ||
    func.type === "subagent" ||
    func.type === "search_models"
  );
};

export const isMcpTool = (func: Function_ | Tool): func is PromptLayerMcpTool =>
  "type" in func && func.type === "mcp" && "mcp_server_id" in func;

export const isToolVariable = (tool: Tool): tool is ToolVariable =>
  "type" in tool && tool.type === "variable";

export const wait = (delay = 0) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, delay);
  });

export function formatMcpError(error: string | Record<string, unknown> | null | undefined): string {
  if (!error) {
    return "";
  }
  if (typeof error === "string") {
    return error;
  }
  return JSON.stringify(error, null, 2);
}
